<template>
  <div class="contact-page">
    <div class="ct-wrap">
      <!-- ===== Header ===== -->
      <div class="ct-header reveal">
        <div class="ct-badge">{{ lg_header_contract.ct_badge }}</div>
        <h1 class="ct-title">{{ lg_header_contract.ct_title }}</h1>
        <p class="text-lg">{{ lg_header_contract.ct_subtitle }}</p>
      </div>

      <!-- ===== Info cards ===== -->
      <div class="ct-info-grid">
        <a
          v-for="info in contactInfo"
          :key="info.key"
          :href="info.link"
          :target="info.key === 'office' ? '_blank' : null"
          class="ct-info-card reveal"
        >
          <div class="ct-info-icon">
            <v-icon size="28" color="#215295">{{ info.icon }}</v-icon>
          </div>
          <h3 class="ct-info-title">{{ lg_header_contract['ct_info_' + info.key] }}</h3>
          <p class="ct-info-content">{{ info.content }}</p>
        </a>
      </div>

      <!-- ===== Form ===== -->
      <div class="ct-form-wrap reveal">
        <div class="ct-form-card">
          <h2 class="ct-form-title">{{ lg_header_contract.ct_form_title }}</h2>

          <form novalidate @submit.prevent="sendEmail">
            <div class="ct-grid-2">
              <div class="ct-field">
                <label for="ct-name">
                  {{ lg_header_contract.ct_form_full_name }} <span class="req">*</span>
                </label>
                <input
                  id="ct-name"
                  v-model="form.name"
                  type="text"
                  :class="{ 'has-error': errors.name }"
                  :placeholder="lg_header_contract.ct_form_full_name_placeholder"
                />
                <small v-if="errors.name" class="ct-error">{{ errors.name }}</small>
              </div>

              <div class="ct-field">
                <label for="ct-email">
                  {{ lg_header_contract.ct_form_email }} <span class="req">*</span>
                </label>
                <input
                  id="ct-email"
                  v-model="form.email"
                  type="email"
                  :class="{ 'has-error': errors.email }"
                  placeholder="your@email.com"
                />
                <small v-if="errors.email" class="ct-error">{{ errors.email }}</small>
              </div>
            </div>

            <div class="ct-grid-2">
              <div class="ct-field">
                <label for="ct-company">{{ lg_header_contract.ct_form_company }}</label>
                <input
                  id="ct-company"
                  v-model="form.company"
                  type="text"
                  :placeholder="lg_header_contract.ct_form_company_placeholder"
                />
              </div>

              <div class="ct-field">
                <label for="ct-phone">{{ lg_header_contract.ct_form_phone }}</label>
                <input
                  id="ct-phone"
                  v-model="form.phone"
                  type="tel"
                  :placeholder="lg_header_contract.ct_form_phone_placeholder"
                />
              </div>
            </div>

            <div class="ct-field">
              <label for="ct-plan">{{ lg_header_contract.ct_form_plan }}</label>
              <select id="ct-plan" v-model="form.plan">
                <option value="free">{{ lg_header_contract.ct_plan_free }}</option>
                <option value="pro">{{ lg_header_contract.ct_plan_pro }}</option>
                <option value="enterprise">{{ lg_header_contract.ct_plan_enterprise }}</option>
                <option value="other">{{ lg_header_contract.ct_plan_other }}</option>
              </select>
            </div>

            <div class="ct-field">
              <label for="ct-message">
                {{ lg_header_contract.ct_form_message }} <span class="req">*</span>
              </label>
              <textarea
                id="ct-message"
                v-model="form.message"
                rows="6"
                :class="{ 'has-error': errors.message }"
                :placeholder="lg_header_contract.ct_form_message_placeholder"
              ></textarea>
              <small v-if="errors.message" class="ct-error">{{ errors.message }}</small>
            </div>

            <button type="submit" class="ct-submit">
              <v-icon size="20" color="white">mdi-send-outline</v-icon>
              {{ lg_header_contract.ct_form_submit }}
            </button>
          </form>
        </div>
      </div>

      <!-- ===== Business hours ===== -->
      <section class="ct-hours reveal">
        <h2 class="ct-section-title">{{ lg_header_contract.ct_hours_title }}</h2>
        <div class="ct-hours-body">
          <p class="text-xl">
            <strong>{{ lg_header_contract.ct_hours_weekdays }}:</strong>
            {{ lg_header_contract.ct_hours_weekdays_time }}
          </p>
          <p class="text-xl">
            <strong>{{ lg_header_contract.ct_hours_weekend }}:</strong>
            {{ lg_header_contract.ct_hours_closed }}
          </p>
          <p class="ct-hours-note">{{ lg_header_contract.ct_hours_email24 }}</p>
        </div>
      </section>
    </div>

    <LoadingDialog></LoadingDialog>
  </div>
</template>

<script>
import api from "@/services/api";
import { server, feature } from "../services/constants";
import LoadingDialog from "@/components/cards/LoadingDialog";
import { lg_header_contract } from "./language/lg_header_contract.js";

const EMAIL_RE =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(([^<>()[\]\\.,;:\s@"]+\.)+[^<>()[\]\\.,;:\s@"]{2,})$/i;

export default {
  name: "Contact",
  components: { LoadingDialog },
  data: () => ({
    lg_header_contract: {},
    form: {
      name: "",
      firstname: "",
      lastname: "",
      email: "",
      phone: "",
      company: "",
      plan: "pro",
      requestType: "",
      message: "",
      email_to: "contact@dcm.co.th",
    },
    errors: { name: "", email: "", message: "" },
    scrollHandler: null,
    timers: [],
  }),
  computed: {
    contactInfo() {
      return [
        {
          key: "email",
          icon: "mdi-email-outline",
          content: "contact@dcm.co.th",
          link: "mailto:contact@dcm.co.th",
        },
        {
          key: "phone",
          icon: "mdi-phone-outline",
          content: "02-2233-0055",
          link: "tel:0222330055",
        },
        {
          key: "office",
          icon: "mdi-map-marker-outline",
          content: this.lg_header_contract.ct_info_address,
          link: "https://maps.app.goo.gl/ENb5whGiHZ7hq7Bz8",
        },
      ];
    },
  },
  created() {
    this.$store.state.page_reload = false;
    this.$store.state.loadFooter = true;
    this.lg_header_contract =
      localStorage.getItem(feature.LANGUAGE) === "th"
        ? lg_header_contract.lg_header_contract_th
        : lg_header_contract.lg_header_contract_en;
  },
  mounted() {
    const userId = localStorage.getItem(server.USER_ID);
    if (userId && api.isLoggedIn()) {
      this.$router.push("/listroom");
      return;
    }

    // animation (เหมือนหน้าอื่น)
    const targets = Array.from(this.$el.querySelectorAll(".reveal"));
    const inView = (el) => {
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const rect = el.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < vh - 100;
    };
    let order = 0;
    targets.forEach((el) => {
      if (inView(el)) {
        this.timers.push(
          setTimeout(() => el.classList.add("reveal--visible"), 200 + order++ * 150)
        );
      }
    });
    this.scrollHandler = () =>
      targets.forEach((el) => el.classList.toggle("reveal--visible", inView(el)));
    document.addEventListener("scroll", this.scrollHandler);
  },
  beforeDestroy() {
    this.cleanup();
  },
  beforeUnmount() {
    this.cleanup();
  },
  methods: {
    cleanup() {
      if (this.scrollHandler) {
        document.removeEventListener("scroll", this.scrollHandler);
        this.scrollHandler = null;
      }
      this.timers.forEach(clearTimeout);
      this.timers = [];
    },
    onClickMenu(link) {
      this.$router.push(link).catch(() => {});
    },
    // กฎเดิม: ชื่อ / อีเมล / ข้อความ บังคับกรอก, เบอร์โทรไม่บังคับ
    validate() {
      const lg = this.lg_header_contract;
      this.errors.name = this.form.name ? "" : lg.rules_name;
      this.errors.email = EMAIL_RE.test(this.form.email) ? "" : lg.rules_email;
      this.errors.message = this.form.message ? "" : lg.rules_message;
      return !this.errors.name && !this.errors.email && !this.errors.message;
    },
    async sendEmail() {
      if (!this.validate()) return;
      try {
        this.$swal.showLoading();
        const response = await api.onUserSendRequestContact(this.form);
        this.$swal.close();
        if (response && response.data && response.data.status === "success") {
          this.$swal.fire("Success", response.data.message, "success");
          this.form.name = "";
          this.form.firstname = "";
          this.form.lastname = "";
          this.form.email = "";
          this.form.phone = "";
          this.form.company = "";
          this.form.plan = "pro";
          this.form.requestType = "";
          this.form.message = "";
        } else {
          this.$swal.fire("Error", response.data.message, "error");
        }
      } catch (error) {
        console.log({ error });
        this.$swal.close();
        this.$swal.fire("Error", error.message, "error");
      }
    },
  },
};
</script>

<style scoped>
.contact-page {
  min-height: 100vh;
  padding: 80px 0;
  background: #ffffff;
  color: #0a0a0a;
}
.ct-wrap {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 16px;
}
@media (min-width: 640px) {
  .ct-wrap { padding: 0 24px; }
}
@media (min-width: 1024px) {
  .ct-wrap { padding: 0 32px; }
}
.text-lg {
  font-size: 1.25rem;
  line-height: 1.75rem;
  color: #4b5563;
  margin: 0;
}
.text-xl {
  font-size: 1.25rem;
  line-height: 1.75rem;
  margin: 0;
}

/* Header */
.ct-header {
  text-align: center;
  margin-bottom: 64px;
}
.ct-badge {
  display: inline-block;
  padding: 8px 16px;
  border-radius: 9999px;
  font-size: 0.875rem;
  margin-bottom: 24px;
  background-color: rgba(33, 82, 149, 0.1);
  color: #215295;
}
.ct-title {
  font-size: 3rem;
  line-height: 1.1;
  font-weight: 500;
  margin: 0 0 16px;
}

/* Info cards */
.ct-info-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
  margin-bottom: 64px;
}
@media (min-width: 1024px) {
  .ct-info-grid { grid-template-columns: repeat(3, 1fr); }
}
.ct-info-card {
  display: block;
  background: #ffffff;
  padding: 32px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  text-align: center;
  text-decoration: none;
  color: inherit !important;
  font-weight: normal !important;
  transition: box-shadow 0.2s ease, opacity 0.8s ease, transform 0.8s ease;
}
.ct-info-card:hover {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1);
}
.ct-info-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  background-color: rgba(33, 82, 149, 0.1);
}
.ct-info-title {
  font-size: 1rem;
  font-weight: 600;
  color: #0a0a0a;
  margin: 0 0 8px;
}
.ct-info-content {
  color: #4b5563;
  margin: 0;
}
.ct-info-card:hover .ct-info-content {
  color: #111827;
}

/* Form */
.ct-form-wrap {
  max-width: 48rem;
  margin: 0 auto;
}
.ct-form-card {
  background: #ffffff;
  padding: 32px;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
}
@media (min-width: 768px) {
  .ct-form-card { padding: 48px; }
}
.ct-form-title {
  font-size: 1.875rem;
  line-height: 2.25rem;
  font-weight: 500;
  text-align: center;
  margin: 0 0 32px;
}
.ct-grid-2 {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
}
@media (min-width: 768px) {
  .ct-grid-2 { grid-template-columns: 1fr 1fr; }
}
.ct-field {
  margin-bottom: 24px;
}
.ct-field label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #0a0a0a;
}
.ct-field .req {
  color: #ef4444;
}
.ct-field input,
.ct-field select,
.ct-field textarea {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font: inherit;
  color: #111827;
  background: #ffffff;
  transition: border-color 0.15s ease;
}
.ct-field select {
  appearance: auto;
}
.ct-field textarea {
  resize: none;
}
.ct-field input:focus,
.ct-field select:focus,
.ct-field textarea:focus {
  outline: none !important;
  border-color: #3b82f6;
}
.ct-field .has-error {
  border-color: #ef4444;
}
.ct-error {
  display: block;
  margin-top: 6px;
  color: #ef4444;
  font-size: 0.8rem;
}
.ct-submit {
  width: 100%;
  padding: 16px 32px;
  border: none;
  border-radius: 8px;
  background: #215295;
  color: #ffffff;
  font: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: opacity 0.15s ease;
}
.ct-submit:hover {
  opacity: 0.9;
}

/* Business hours */
.ct-hours {
  margin-top: 80px;
  padding: 64px 32px;
  border-radius: 16px;
  text-align: center;
  background: rgba(33, 82, 149, 0.05);
}
.ct-section-title {
  font-size: 1.875rem;
  line-height: 2.25rem;
  font-weight: 500;
  margin: 0 0 16px;
}
.ct-hours-body {
  max-width: 42rem;
  margin: 0 auto;
  color: #4b5563;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.ct-hours-note {
  font-size: 1.125rem;
  margin: 8px 0 0;
}

@media (max-width: 599px) {
  .ct-title { font-size: 2.25rem; }
}

/* Animation */
.reveal {
  opacity: 0;
  transform: translateY(40px);
  transition: opacity 0.8s ease, transform 0.8s ease;
}
.reveal--visible {
  opacity: 1;
  transform: translateY(0);
}
@media (prefers-reduced-motion: reduce) {
  .reveal {
    opacity: 1;
    transform: none;
    transition: none;
  }
}
</style>